import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Checkbox, CircularProgress, FormControlLabel, Grid, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState, useRef } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import {
  ClientContactDTO,
  autocompleteClientRecruiterName,
  findByEmailAndClientId,
  saveActivityLog,
  LogEntityType,
  LogContextType,
  LogAction,
  csrfTokenCall,
  getUserByEmail,
  uploadFileToS3,
  FieldTypeEnum,
  CustomFieldEnum,
} from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsAutocompleteInput,
  WsSelect,
  WsSelectObj,
  WsLocationSearch,
  Label,
  WsFileUpload,
  FileTypeVariants,
  WsColors,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../../components/ClientAutocomplete';
import RecruiterSuggestions from '../../../../components/filters/components/RecruiterSuggestions';
import WsTagsFilter from '../../../../components/filters/components/WsTagsFilter';
import {
  activityLogDTOHelper,
  getAvEditAddDesUiName,
} from '../../../../features/activityLog/ActivityHelper';
import { fetchNameAndEmailFromClientContact } from '../../../../features/jobs/helper';
import { failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import {
  buildClientContactValidationSchema,
  isFieldRequired,
} from '../../../clientContacts/helper/ContactListingHelper';
import { linkedInRegex, EntityType } from '../../helper/ClientListingHelper';
import { useCustomFields } from '../../hooks/useCustomFields';
import CustomField from '../CustomField';

interface ClientContactFormProps {
  clientId: string;
  onSubmit: (data: ClientContactDTO, isShareable: boolean) => void;
  onBack?: () => void;
  onSkip?: () => void;
  onContinue?: () => void;
  closeOnUpdate?: () => void;
  clientContact: ClientContactDTO;
  saveButtonLabel?: string;
  source?: 'JobPostingForm' | 'Addition';
  clientOwner?: { name: string; email: string } | null;
  clientName?: string | null;
  isClientContactList?: boolean;
}

const statusList: WsSelectObj[] = [
  {
    label: 'Active',
    value: 'Active',
  },
  {
    label: 'Inactive',
    value: 'Inactive',
  },
];

const ClientContactForm = ({
  clientId,
  onSubmit,
  onBack,
  onSkip,
  onContinue,
  closeOnUpdate,
  clientContact,
  saveButtonLabel = 'Save and Add More',
  source = 'Addition',
  clientOwner,
  clientName,
  isClientContactList,
}: ClientContactFormProps) => {
  const { customFields, defaultFields } = useCustomFields(CustomFieldEnum.CONTACT);

  const methods = useForm<any>({
    defaultValues: {
      ...clientContact,
      tags: clientContact?.tags ?? [],
      inheritUserAccess: false,
      client: clientName,
      entityType: clientContact?.entityType || [EntityType.PROSPECT],
    },
    resolver: yupResolver(
      buildClientContactValidationSchema([...(customFields ?? []), ...(defaultFields ?? [])])
    ),
  });
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { dirtyFields },
  } = methods;
  const [selectedSupervisor, setSelectedSupervisor] = useState<ClientContactDTO | null>();
  const clientContactList = useRef<ClientContactDTO[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');
  const [uploadedImage, setUploadedImage] = useState<string | null>(
    clientContact?.imageUrl ?? null
  );

  const selectedClient = watch('client');

  useEffect(() => {
    setValue('supervisorEmailAddress', null);
    setSelectedSupervisor(null);
  }, [selectedClient, setValue]);

  const handleOwnerSelection = async (emails: string[], onChange: (val: string) => void) => {
    const selectedEmail = emails[0] || '';
    onChange(selectedEmail);
    if (selectedEmail) {
      try {
        const res = await getUserByEmail(selectedEmail);
        if (res?.data) {
          const firstName = res.data.firstName || '';
          const lastName = res.data.lastName || '';
          const recruiterName = `${firstName}${lastName ? ' ' + lastName : ''}`.trim();
          methods.setValue('owner', { name: recruiterName, email: selectedEmail });
        } else {
          methods.setValue('owner', null);
        }
      } catch (error) {
        console.error('Failed to fetch recruiter details:', error);
        methods.setValue('owner', null);
      }
    } else {
      methods.setValue('owner', null);
    }
  };

  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);

  useEffect(() => {
    if (clientContact) {
      Object.entries(clientContact).forEach(([name, value]) =>
        setValue(name as keyof ClientContactDTO, value)
      );
      if (clientContact.supervisor) {
        const { emailAddress, position, firstName, lastName } = clientContact.supervisor;
        setValue('supervisorEmailAddress', emailAddress);
        setValue('supervisorPosition', position);
        setValue('supervisorName', `${firstName} ${lastName}`);
        setSelectedSupervisor(clientContact.supervisor);
      }
      setValue('markStatus', clientContact.markStatus == true ? 'Active' : 'Inactive');
      setSelectedSupervisor(clientContact.supervisor);
    }
  }, [clientContact, setValue]);

  const onClientContactInputKey = async (cContact: string) => {
    const result = await autocompleteClientRecruiterName(
      `${encodeURIComponent(cContact)
        .trim()
        .split(' ')
        .map((contact) => contact + ':*')
        .join(' & ')}`,
      clientId,
      clientContact?.id
    );

    if (result?.data.length > 0) {
      clientContactList.current = result.data;
      return fetchNameAndEmailFromClientContact(result.data) ?? [];
    }
    return [];
  };

  const onChange = (event) => {
    const fetchedEmail = event?.split('-')[1]?.trim().split('|')[0];

    const foundClientContact = clientContactList.current?.filter(
      (client) => client.emailAddress === fetchedEmail
    );
    setValue('supervisorEmailAddress', fetchedEmail);
    setValue('supervisorPosition', foundClientContact[0].position);
    setValue(
      'supervisorName',
      foundClientContact[0].firstName + ' ' + foundClientContact[0].lastName
    );
    setSelectedSupervisor(foundClientContact[0]);
  };

  const handleContinueClick = () => {
    if (
      !isEmpty(methods.getValues('linkedinProfile')) &&
      !linkedInRegex.test(methods.getValues('linkedinProfile'))
    ) {
      setNotification(failed('Please provide a valid LinkedIn URL'));
      return;
    }
    if (isEmpty(methods.getValues('firstName')) && isEmpty(methods.getValues('emailAddress'))) {
      setNotification(failed('Please complete details and then continue!!'));
      return;
    }

    const isShareable = watch('inheritUserAccess');
    handleContinue(isShareable);
  };

  const checkDuplicateContact = async (email: string) => {
    const response = await findByEmailAndClientId(email, clientId);
    return response.data.data && response.data.data.id != clientContact.id;
  };

  const submit = async (data, isShareable) => {
    setIsLoading(true);
    if (await checkDuplicateContact(data.emailAddress)) {
      setNotification(failed('Contact already exists for this email!!'));
      setIsLoading(false);
      return;
    }
    if (source === 'Addition') data.supervisor = selectedSupervisor;
    data.markStatus = watch('markStatus') == 'Active' ? true : false;
    if (!data.owner) {
      data.owner = clientOwner;
    }
    const res = await onSubmit({ ...data, clientId: data?.client?.id ?? clientId }, isShareable);
    const avPayload = { ...activityLogDTOHelper };
    const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
    if (data.id) {
      avPayload.action = LogAction.Edit;
      const avEmptyEntityfields = Object.keys(
        Object.entries(clientContact).reduce((acc, [k, v]) => {
          if (v === '' || v === null) acc[k] = v;
          return acc;
        }, {})
      );
      avPayload.description = `${getAvEditAddDesUiName(
        'Client',
        avEmptyEntityfields,
        avChangedValueViaDirtyFileds
      )} for Client Contact ${data.firstName} under`;
    } else {
      avPayload.action = LogAction.Add;
      avPayload.description = `ADDED ${data.firstName} for Client Contact under`;
    }
    avPayload.parentEntitiyType = LogContextType.Client;
    avPayload.entityType = LogEntityType.ClientContact;
    avPayload.entityId = res as unknown as string;
    avPayload.parentEntitiyId = data?.client?.id ?? clientId;
    avPayload.field = `Client Contact`;
    avPayload.entityName = data.firstName;
    avPayload.contextData = { clientId: data?.client?.id ?? clientId };
    avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
    setIsLoading(false);
  };

  const handleContinue = async (isShareable) => {
    try {
      if (await checkDuplicateContact(methods.getValues('emailAddress'))) {
        setNotification(failed('Contact already exists for this email!!'));
        return;
      }

      if (methods.formState.isDirty) {
        setNotification(failed('Please complete details and then continue!!'));
        return;
      }
      const result = await methods.trigger();
      if (!result) return;
      if (
        !methods.formState.isDirty &&
        !isEmpty(methods.getValues('firstName')) &&
        !isEmpty(methods.getValues('emailAddress'))
      ) {
        const updatedClientContact = { ...clientContact, ...methods.getValues() };
        await submit(updatedClientContact, isShareable);
      }
      onContinue && onContinue();
    } catch (error) {
      console.error('Error in client contact form');
      setNotification(failed('Something went wrong. Please try again.'));
    }
  };

  const onUpload = async (file: File[]) => {
    try {
      const url = await uploadFileToS3(file[0]);
      if (url) {
        methods.setValue('imageUrl', removeQueryParams(url));
        setUploadedImage(removeQueryParams(url));
      }
    } catch (error) {
      console.error('Error uploading file to S3:', error);
      setNotification(failed('Failed to upload file to S3'));
    }
  };

  const removeQueryParams = (url: string) => {
    const urlObj = new URL(url);
    urlObj.search = '';
    return urlObj.toString();
  };

  const getExtension = (url: string) => {
    const urlObj = new URL(url);
    return urlObj.pathname.split('.').pop();
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => submit(data, watch('inheritUserAccess')))}>
          <Box borderRadius={1.5}>
            <Grid container py={1}>
              <Grid item p={1} sm={5.75}>
                {isClientContactList && (
                  <ClientAutocomplete name="client" placeHolder="Select Client *" required={true} />
                )}
              </Grid>
              <Grid item sm={6.25} />
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="firstName"
                  type="text"
                  fullWidth
                  labelText="First Name"
                  variant="outlined"
                  showMandatorySymbol={isFieldRequired('firstName', defaultFields)}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="lastName"
                  type="text"
                  fullWidth
                  labelText="Last Name"
                  variant="outlined"
                  showMandatorySymbol={isFieldRequired('lastName', defaultFields)}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="emailAddress"
                  type="text"
                  fullWidth
                  labelText="Email Address"
                  variant="outlined"
                  showMandatorySymbol={isFieldRequired('emailAddress', defaultFields)}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsLocationSearch
                  multiSelect={false}
                  name="location"
                  placeHolder="Location"
                  wisestepApi={true}
                  token={token}
                  authorization={auth}
                  showMandatorySymbol={isFieldRequired('location', defaultFields)}
                />
              </Grid>
              {source === 'Addition' && (
                <Grid item p={1} sm={5.75}>
                  <ControlledTextInput
                    name="phoneNumber"
                    type="text"
                    fullWidth
                    labelText="Phone Number"
                    variant="outlined"
                    showMandatorySymbol={isFieldRequired('phoneNumber', defaultFields)}
                  />
                </Grid>
              )}
              <Grid item p={1} sm={5.75}>
                <WsTagsFilter
                  placeHolder="Enter Tags"
                  name="tags"
                  label="Tags"
                  showMandatorySymbol={isFieldRequired('tags', defaultFields)}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="position"
                  type="text"
                  fullWidth
                  labelText="Position"
                  variant="outlined"
                  showMandatorySymbol={isFieldRequired('position', defaultFields)}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsSelect
                  label="Client Contact Status"
                  name="markStatus"
                  selectArray={statusList}
                  defaultValue={clientContact.markStatus == true ? 'Active' : 'Inactive'}
                  size="small"
                  showMandatorySymbol={isFieldRequired('markStatus', defaultFields)}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <Label>{`${'Client Contact Owner'} ${
                  isFieldRequired('ownerId', defaultFields) ? '*' : ''
                }`}</Label>
                <Controller
                  name="ownerId"
                  control={methods.control}
                  defaultValue={clientContact.owner?.email ?? clientOwner?.email}
                  render={({ field }) => (
                    <RecruiterSuggestions
                      {...field}
                      value={field.value || ''}
                      defaultValue={field.value ? [field.value] : []}
                      onSelectMultipleValues={(emails: string[]) =>
                        handleOwnerSelection(emails, field.onChange)
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="linkedinProfile"
                  type="text"
                  fullWidth
                  labelText="Linkedin Profile"
                  showMandatorySymbol={isFieldRequired('linkedinProfile', defaultFields)}
                />
              </Grid>
              {source === 'Addition' && (
                <>
                  <Grid item p={1} sm={5.75}>
                    <WsAutocompleteInput
                      name="supervisorEmailAddress"
                      label="Supervisor"
                      options={[]}
                      placeholder={'Eg: alice@gmail.com'}
                      getOptions={onClientContactInputKey}
                      onChange={onChange}
                      key={selectedSupervisor?.emailAddress}
                      showMandatorySymbol={isFieldRequired('supervisorEmailAddress', defaultFields)}
                    />
                  </Grid>
                  {selectedSupervisor && (
                    <>
                      <Grid item p={1} sm={5.75}>
                        <ControlledTextInput
                          name="supervisorPosition"
                          type="text"
                          fullWidth
                          labelText="Supervisor Position"
                          disabled
                        />
                      </Grid>
                      <Grid item p={1} sm={5.75}>
                        <ControlledTextInput
                          name="supervisorName"
                          type="text"
                          fullWidth
                          labelText="Supervisor Name"
                          disabled
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item p={1} sm={5.75} mt={3.5}>
                    <Controller
                      name="inheritUserAccess"
                      control={methods.control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox {...field} color="primary" />}
                          label="Inherit creator's user data access to the client contact"
                        />
                      )}
                    />
                  </Grid>
                  {customFields &&
                    customFields.map(
                      (field) =>
                        field.visible && (
                          <Grid
                            item
                            key={field.id}
                            p={1}
                            sm={field.type === FieldTypeEnum.Checkbox ? 11.5 : 5.75}
                          >
                            <CustomField
                              {...field}
                              name={`customFields.${field.name}`}
                              token={token}
                              auth={auth}
                            />
                          </Grid>
                        )
                    )}
                  <Grid item p={1} sm={5.75}>
                    {uploadedImage ? (
                      <Grid direction="row" spacing={2} mb={2}>
                        <WsTypography variant={WsTypoVariants.BODY1}>
                          {`profile.${getExtension(uploadedImage)}`}
                        </WsTypography>
                        <CloseIcon
                          onClick={() => {
                            setUploadedImage(null);
                            methods.setValue('imageUrl', null);
                          }}
                          sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                        />
                      </Grid>
                    ) : (
                      <WsFileUpload
                        multiple={false}
                        fileTypes={FileTypeVariants.IMAGES}
                        onUpload={(file: File[]) => onUpload(file)}
                        name={'imageUrl'}
                        placeholderText=""
                        bgRequired={false}
                        buttonText={'Upload Profile Image'}
                        maxFileSize={3}
                      />
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
          <Stack
            p={1}
            spacing={2}
            direction="row"
            display="flex"
            justifyContent={closeOnUpdate ? 'flex-end' : 'flex-end'}
          >
            {closeOnUpdate && (
              <WsButton size="medium" onClick={() => onContinue && onContinue()}>
                {'Cancel'}
              </WsButton>
            )}
            <WsButton
              size="medium"
              type="submit"
              disabled={isLoading}
              endIcon={
                isLoading && saveButtonLabel != 'Add More' ? (
                  <CircularProgress size={17} color="secondary" />
                ) : undefined
              }
            >
              {saveButtonLabel == 'Add More' && isLoading
                ? saveButtonLabel
                : isLoading
                ? 'Saving...'
                : saveButtonLabel}
            </WsButton>
          </Stack>

          <Stack p={1} spacing={2} direction="row" display="flex" justifyContent="right">
            {!closeOnUpdate && onBack && (
              <WsButton variant="contained" size="medium" onClick={() => onBack()}>
                {'Back'}
              </WsButton>
            )}
            {!closeOnUpdate && onSkip && (
              <WsButton variant="contained" size="medium" onClick={() => onSkip()}>
                {'Skip'}
              </WsButton>
            )}
            {!closeOnUpdate && onContinue && (
              <WsButton
                size="medium"
                onClick={handleContinueClick}
                disabled={isLoading}
                endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : undefined}
              >
                {isLoading ? 'Saving...' : 'Continue'}
              </WsButton>
            )}
          </Stack>
        </form>
      </FormProvider>
    </>
  );
};

export default ClientContactForm;
